/**
 * Scientific capability cards.
 *
 * Converted verbatim from `public/index.html:196-352`.
 */

const CapabilityGrid = () => (
  <section className="capabilities-section">
    <div className="section-header">
      <h2 className="section-title">Scientific Capabilities</h2>
      <p className="section-subtitle">
        Unleashing new possibilities for lung biology research
      </p>
    </div>

    <div className="capabilities-grid">
      <div className="capability-card">
        <div className="capability-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
          </svg>
        </div>
        <h3 className="capability-title">Comprehensive Single-Cell Analysis</h3>
        <p className="capability-description">
          Query 2.8M+ cells from 470+ donors across 40+ studies covering normal
          lung, development, and 15+ diseases (IPF, COPD, COVID-19, BPD, lung
          cancer). Generate publication-ready UMAPs, heatmaps, violin plots,
          volcano plots, dotplots, and differential expression analyses. Perform
          cross-dataset comparisons to identify conserved vs. disease-specific
          signatures, with statistical cell type proportion testing and
          interactive web-based heatmap exploration.
        </p>
      </div>

      <div className="capability-card">
        <div className="capability-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        </div>
        <h3 className="capability-title">
          Spatial Transcriptomics & Cell Communication
        </h3>
        <p className="capability-description">
          Analyze spatial data from Xenium and Visium platforms to map cellular
          niches and tissue architecture. Explore cell-cell communication with
          CellChat—visualize ligand-receptor interactions as chord diagrams and
          signaling pathway networks. Perform spatial neighborhood enrichment
          with Squidpy, cross-platform Xenium-Visium correlation analysis, and
          integrate H&E/DAPI histology for multi-modal figures.
        </p>
      </div>

      <div className="capability-card">
        <div className="capability-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
            <path d="M12 5 9.04 7.96a2.17 2.17 0 0 0 0 3.08v0c.82.82 2.13.85 3 .07l2.07-1.9a2.82 2.82 0 0 1 3.79 0l2.96 2.66" />
            <path d="m18 15-2-2" />
            <path d="m15 18-2-2" />
          </svg>
        </div>
        <h3 className="capability-title">Drug Discovery & Repurposing</h3>
        <p className="capability-description">
          Identify potential therapeutics using connectivity mapping against
          143K+ perturbation profiles and DART, LungChat's Direction-Aware
          Repurposing and Targeting method. Find compounds that could reverse
          disease gene signatures, investigate mechanisms of action, explore
          gene knockout effects, and profile cell-type-specific efficacy and
          off-target safety risks.
        </p>
      </div>

      <div className="capability-card">
        <div className="capability-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
        </div>
        <h3 className="capability-title">Functional Enrichment & Literature</h3>
        <p className="capability-description">
          Perform pathway enrichment using 120+ databases (GO, KEGG, Reactome,
          disease ontologies, drug targets) to identify biological processes,
          molecular functions, and disease associations. Search PubMed with
          intelligent relevance ranking that prioritizes clinical trials and
          experimental evidence. Synthesize findings to contextualize
          discoveries and connect pathways to disease mechanisms.
        </p>
      </div>

      <div className="capability-card">
        <div className="capability-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </div>
        <h3 className="capability-title">Advanced Visualization Suite</h3>
        <p className="capability-description">
          Create gene-gene interaction networks, correlation matrices, and
          Venn/UpSet diagrams for gene set comparisons. Generate radar charts
          for cell composition, box plots with statistical testing for cell
          frequency analysis, and multi-panel spatial figures combining Xenium,
          Visium, H&E and DAPI. All visualizations are publication-ready in PNG
          and PDF formats.
        </p>
      </div>

      <div className="capability-card">
        <div className="capability-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
        </div>
        <h3 className="capability-title">LungMAP Database Integration</h3>
        <p className="capability-description">
          Query the LungMAP consortium database through natural language to
          access comprehensive lung development and disease resources. Discover
          datasets, explore sample demographics, retrieve gene lists from
          computational analyses, and perform cross-species comparisons for
          developmental trajectory analysis.
        </p>
      </div>

      <div className="capability-card">
        <div className="capability-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        </div>
        <h3 className="capability-title">Custom Computational Analysis</h3>
        <p className="capability-description">
          Execute custom Python or R code in a secure sandbox for novel
          analyses, statistical computations, data transformations, and custom
          visualizations. Process TSV/CSV files, perform statistical tests, and
          create publication-quality plots tailored to your specific research
          questions.
        </p>
      </div>

      <div className="capability-card">
        <div className="capability-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
        </div>
        <h3 className="capability-title">Reproducibility & Provenance</h3>
        <p className="capability-description">
          Every analysis generates publication-ready figures (PNG/PDF), data
          tables (TSV), and complete parameter logs (JSON) ensuring full
          reproducibility and scientific rigor. All outputs are downloadable for
          publication, sharing, and exact replication of analyses.
        </p>
      </div>
    </div>
  </section>
);

export default CapabilityGrid;
